import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type HostProfileData,
  type EmailUsageSummary,
  type NotificationPrefsData,
  type BillingRecord,
} from '../api/client';

// ─── Profile ─────────────────────────────────────────────────────────────────

export function useHostProfile() {
  return useQuery<HostProfileData>({
    queryKey: ['account', 'profile'],
    queryFn: () => api.account.getProfile(),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; email?: string }) =>
      api.account.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'profile'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.account.changePassword(data),
  });
}

// ─── Email Usage ─────────────────────────────────────────────────────────────

export function useEmailUsage() {
  return useQuery<EmailUsageSummary>({
    queryKey: ['account', 'email-usage'],
    queryFn: () => api.account.getEmailUsage(),
  });
}

// ─── Notification Preferences ────────────────────────────────────────────────

export function useNotificationPrefs() {
  return useQuery<NotificationPrefsData>({
    queryKey: ['account', 'notifications'],
    queryFn: () => api.account.getNotifications(),
  });
}

export function useUpdateNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      emailOnRsvp?: boolean;
      emailOnReminder?: boolean;
      emailOnBilling?: boolean;
      emailDigest?: string;
    }) => api.account.updateNotifications(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'notifications'] });
    },
  });
}

// ─── Billing History ─────────────────────────────────────────────────────────

export function useBillingHistory() {
  return useQuery<BillingRecord[]>({
    queryKey: ['account', 'billing-history'],
    queryFn: () => api.account.getBillingHistory(),
  });
}
